import { Container, Graphics, type FederatedPointerEvent } from "pixi.js";
import {
  type ChunkData,
  type Facing,
  type MobSnapshot,
  type PlayerSnapshot,
  type Position,
  type ProjectileSnapshot,
  TILE_H,
  TILE_W,
} from "@vinyl/shared";
import { createPixiSurface, type PixiSurface } from "./app.js";
import { EntityLayer, type GroundBagSnapshot } from "./entities.js";
import { type LayerSlot } from "./player-sprite.js";
import { pixelToTile, tileToScreen } from "./iso.js";
import {
  AreaEffectLayer,
  type AreaEffectSpawnPayload,
} from "./area-effects.js";
import {
  ProjectileLayer,
  type ProjectileExpirePayload,
  type ProjectileSpawnPayload,
} from "./projectiles.js";
import { ChunkedTilemap, highlightTile } from "./tilemap.js";
import { DecorLayer } from "./decor.js";

// M14 — global camera zoom. Multiplier applied to the world container,
// so tiles and sprites render this many times larger than their native
// pixel size. 1.0 = native (Age-of-Empires-style wide strategic view);
// 1.5–2.0 = closer to a classic action-RPG over-the-shoulder feel.
// Tune to taste; eventually exposed as a player setting (zoom in/out).
const WORLD_SCALE = 1.6;

// M11 — minimal zone metadata replacing the full TilemapJson previously
// embedded in zoneEnter. Terrain arrives separately via chunkData.
export interface ZoneEnterMeta {
  zoneId: string;
  width: number;
  height: number;
  isSafe: boolean;
}

export interface WorldViewCallbacks {
  onPointerDown?: (tx: number, ty: number) => void;
  onPointerMove?: (tx: number, ty: number) => void;
  onPointerUp?: () => void;
}

interface PendingZone {
  meta: ZoneEnterMeta;
  selfId: string;
  selfName: string;
  pos: Position;
  facing: Facing;
}

export class WorldView {
  private surface: PixiSurface | null = null;
  private chunkedTilemap: ChunkedTilemap | null = null;
  private readonly decorLayer = new DecorLayer();
  private highlight = new Container();
  private targetRing = new Container();
  private targetRingGraphic: Graphics | null = null;
  private readonly entities = new EntityLayer();
  private readonly projectiles = new ProjectileLayer();
  private readonly areaEffects = new AreaEffectLayer();
  private localId: string | null = null;
  private targetMobId: string | null = null;
  private predictedX = 0;
  private predictedY = 0;
  private predictedFacing: Facing = "S";
  private destroyed = false;
  private highlightFadeAt = 0;
  private pendingZone: PendingZone | null = null;
  private pendingNpcs: import("@vinyl/shared").NpcSnapshot[] | null = null;
  // M11 — chunks arriving before applyZone (race: chunkData can land in
  // the same WS tick as zoneEnter, and Pixi surface init is async). Same
  // pattern as pendingZone / pendingNpcs — buffer and replay post-init.
  private pendingChunks: ChunkData[] = [];
  private pointerDownId: number | null = null;

  constructor(
    private readonly mount: HTMLElement,
    private readonly callbacks: WorldViewCallbacks,
  ) {}

  async init(): Promise<void> {
    const surface = await createPixiSurface(this.mount);
    if (this.destroyed) {
      surface.destroy();
      return;
    }
    this.surface = surface;
    // M14 — apply global zoom by scaling the world container. All children
    // (terrain, decor, entities, projectiles) inherit the scale; we only
    // need to compensate in the camera-centering math (tick) and the
    // screen-to-tile math (eventToTile) where scale matters.
    surface.world.scale.set(WORLD_SCALE);
    this.highlight.label = "highlight";
    this.targetRing.label = "target-ring";
    this.targetRing.visible = false;
    // M14 — decorLayer added FIRST so applyZone's `addChildAt(chunkedTilemap, 0)`
    // pushes decor to index 1, landing just above terrain and below
    // highlight / target-ring / AOE / entities / projectiles. This puts
    // players in front of trees consistently — proper cross-layer iso
    // depth-sort (tree behind/in front of player based on ty) is a
    // follow-up that requires unifying decor + entities under one
    // sortable container.
    surface.world.addChild(this.decorLayer.container);
    surface.world.addChild(this.highlight);
    surface.world.addChild(this.targetRing);
    // AOE rings sit beneath entities so sprites render above the visual.
    surface.world.addChild(this.areaEffects.container);
    surface.world.addChild(this.entities.container);
    surface.world.addChild(this.projectiles.container);
    this.targetRingGraphic = makeTargetRing();
    this.targetRing.addChild(this.targetRingGraphic);
    surface.app.ticker.add(this.tick);
    surface.app.canvas.addEventListener("pointerdown", this.handlePointerDown);
    surface.app.canvas.addEventListener("pointermove", this.handlePointerMove);
    surface.app.canvas.addEventListener("pointerup", this.handlePointerUp);
    surface.app.canvas.addEventListener("pointercancel", this.handlePointerUp);
    if (this.pendingZone) {
      this.applyZone(this.pendingZone);
      this.pendingZone = null;
    }
    if (this.pendingNpcs) {
      this.entities.ingestNpcsInZone(this.pendingNpcs);
      this.pendingNpcs = null;
    }
    if (this.pendingChunks.length > 0 && this.chunkedTilemap) {
      for (const c of this.pendingChunks) {
        this.chunkedTilemap.ingest(c);
        this.decorLayer.ingest(c.cx, c.cy, c.decor);
      }
      this.pendingChunks = [];
    }
  }

  setZone(meta: ZoneEnterMeta, selfCharacterId: string, selfName: string, pos: Position, facing: Facing): void {
    const params: PendingZone = { meta, selfId: selfCharacterId, selfName, pos, facing };
    if (!this.surface) {
      this.pendingZone = params;
      return;
    }
    this.applyZone(params);
  }

  // M11 — terrain arrives in chunks, each ingested independently. Chunks
  // for a stale zone (zone changed between request and arrival) are
  // silently dropped by the connected.ts layer before reaching here.
  // M14 — decor placements ride along with the chunk and are handed off
  // to the DecorLayer.
  ingestChunk(chunk: ChunkData): void {
    if (!this.chunkedTilemap) {
      this.pendingChunks.push(chunk);
      return;
    }
    this.chunkedTilemap.ingest(chunk);
    this.decorLayer.ingest(chunk.cx, chunk.cy, chunk.decor);
  }

  private applyZone(p: PendingZone): void {
    if (!this.surface) return;
    if (this.chunkedTilemap) {
      this.surface.world.removeChild(this.chunkedTilemap.container);
      this.chunkedTilemap.destroy();
    }
    this.entities.clearRemotes();
    this.projectiles.clear();
    this.areaEffects.clear();
    // M14 — drop decor sprites from the previous zone. The container
    // itself stays parented to surface.world (added once in init); only
    // its per-chunk children get cleared.
    this.decorLayer.clear();
    this.setTargetMob(null);
    this.chunkedTilemap = new ChunkedTilemap();
    this.surface.world.addChildAt(this.chunkedTilemap.container, 0);
    // Drain any chunks that landed between setZone and surface init.
    if (this.pendingChunks.length > 0) {
      for (const c of this.pendingChunks) {
        this.chunkedTilemap.ingest(c);
        this.decorLayer.ingest(c.cx, c.cy, c.decor);
      }
      this.pendingChunks = [];
    }
    this.localId = p.selfId;
    this.predictedX = p.pos.x;
    this.predictedY = p.pos.y;
    this.predictedFacing = p.facing;
    this.entities.ensureLocal(p.selfId, p.selfName);
    this.entities.setLocalPosition(p.pos.x, p.pos.y, p.facing, true);
  }

  ingestSnapshot(snapshots: PlayerSnapshot[], now = performance.now()): void {
    if (!this.localId) return;
    this.entities.ingestSnapshot(snapshots, this.localId, now);
  }

  ingestMobSnapshot(snapshots: MobSnapshot[], serverTime: number, now = performance.now()): void {
    this.entities.ingestMobSnapshot(snapshots, now, serverTime);
  }

  ingestBags(snapshots: GroundBagSnapshot[]): void {
    this.entities.ingestBagsSnapshot(snapshots);
  }

  ingestNpcs(snapshots: import("@vinyl/shared").NpcSnapshot[]): void {
    if (!this.surface) {
      this.pendingNpcs = snapshots;
      return;
    }
    this.entities.ingestNpcsInZone(snapshots);
  }

  showNpcSpeech(npcId: string, text: string, durationMs?: number): void {
    this.entities.showNpcSpeech(npcId, text, durationMs);
  }

  spawnProjectile(payload: ProjectileSpawnPayload, perfNow = performance.now()): void {
    this.projectiles.spawn(payload, perfNow);
  }

  ingestProjectiles(snapshots: ProjectileSnapshot[], perfNow = performance.now()): void {
    this.projectiles.ingestSnapshot(snapshots, perfNow);
  }

  expireProjectile(payload: ProjectileExpirePayload, perfNow = performance.now()): void {
    this.projectiles.expire(payload, perfNow);
  }

  spawnAreaEffect(payload: AreaEffectSpawnPayload, perfNow = performance.now()): void {
    this.areaEffects.spawn(payload, perfNow);
  }

  expireAreaEffect(areaId: string): void {
    this.areaEffects.expire(areaId);
  }

  setPredicted(pos: Position, facing: Facing, snap = false): void {
    this.predictedX = pos.x;
    this.predictedY = pos.y;
    this.predictedFacing = facing;
    this.entities.setLocalPosition(pos.x, pos.y, facing, snap);
  }

  getPredicted(): { pos: Position; facing: Facing } {
    return { pos: { x: this.predictedX, y: this.predictedY }, facing: this.predictedFacing };
  }

  flashHighlight(tx: number, ty: number, durationMs = 500): void {
    highlightTile(this.highlight, tx, ty);
    this.highlightFadeAt = performance.now() + durationMs;
  }

  setTargetMob(mobId: string | null): void {
    this.targetMobId = mobId;
    this.targetRing.visible = mobId !== null;
  }

  setPlayerBuffGlow(characterId: string, color: number | null): void {
    this.entities.setPlayerBuffGlow(characterId, color);
  }

  // Plays the local player's punch one-shot. Called from onCombatHit when the
  // local player is the attacker. Pass targetId to make the character face
  // the attacked entity (target tile - local tile → facing). Safe to call
  // before the sprite controller has finished loading (no-op).
  triggerLocalPunch(targetId?: string): void {
    this.entities.triggerLocalPunch(targetId);
  }

  // Fired in onCombatHit when the local player melee-hits while a 1H weapon
  // is in RightHand. Pass targetId to make the swing face the target tile.
  triggerLocalAttack1h(targetId?: string): void {
    this.entities.triggerLocalAttack1h(targetId);
  }

  // Fired in onCombatHit when the local player melee-hits while a 2H weapon
  // occupies RightHand + LeftHand. Pass targetId to make the swing face it.
  triggerLocalAttack2h(targetId?: string): void {
    this.entities.triggerLocalAttack2h(targetId);
  }

  // Show/hide a local player equipment layer (helmet, main_hand, …) based on
  // the equipped item. Safe before the sprite controller loads (deferred).
  setLocalLayer(slot: LayerSlot, spriteId: string | null): void {
    this.entities.setLocalLayer(slot, spriteId);
  }

  // Swap the local player's BODY variant (e.g. torso armour baked in). Pass the
  // base body name to revert. Safe before the sprite controller loads (deferred).
  setLocalBody(bodyName: string): void {
    this.entities.setLocalBody(bodyName);
  }

  // Plays the local player's casting one-shot. Called right after a castSpell
  // message is sent so the player gets immediate visual feedback. Pass the
  // clicked tile to rotate the character toward where the spell is going;
  // omit for self-cast. Safe to call before the controller has loaded (no-op).
  triggerLocalCast(targetTile?: { x: number; y: number }): void {
    this.entities.triggerLocalCast(targetTile);
  }

  setAggroOverlayVisible(visible: boolean): void {
    this.entities.setAggroOverlayVisible(visible);
  }

  isAggroOverlayVisible(): boolean {
    return this.entities.isAggroOverlayVisible();
  }

  setMobDebugVisible(visible: boolean): void {
    this.entities.setMobDebugVisible(visible);
  }

  isMobDebugVisible(): boolean {
    return this.entities.isMobDebugVisible();
  }

  // M17.4 — Scholar's Eye: show exact level + HP on every mob.
  setScholarsEye(on: boolean): void {
    this.entities.setScholarsEye(on);
  }

  applyMobDebugSnapshot(mobs: readonly { id: string; losMissCount: number; swingAt: number }[]): void {
    this.entities.applyMobDebugSnapshot(mobs);
  }

  bubbleLayer(): HTMLElement | null {
    return this.surface?.bubbleLayer ?? null;
  }

  worldToScreen(tx: number, ty: number): { sx: number; sy: number } | null {
    if (!this.surface) return null;
    const iso = tileToScreen(tx, ty);
    return {
      sx: iso.sx + this.surface.world.x,
      sy: iso.sy + this.surface.world.y,
    };
  }

  entityScreenPos(id: string): { sx: number; sy: number } | null {
    if (!this.surface) return null;
    const local = this.entities.getScreenPos(id);
    if (!local) return null;
    // local.sx/sy are pre-scale world coords. The world container has
    // scale = WORLD_SCALE, so on-screen position is (local * SCALE + worldPos).
    // Previously this returned (local + worldPos) which was off by
    // local * (SCALE - 1) — barely visible for small CSS offsets used by
    // damage numbers, but obviously wrong for the chat bubble (95px offset).
    return {
      sx: local.sx * WORLD_SCALE + this.surface.world.x,
      sy: local.sy * WORLD_SCALE + this.surface.world.y,
    };
  }

  destroy(): void {
    this.destroyed = true;
    if (!this.surface) return;
    this.surface.app.ticker.remove(this.tick);
    this.surface.app.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.surface.app.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.surface.app.canvas.removeEventListener("pointerup", this.handlePointerUp);
    this.surface.app.canvas.removeEventListener("pointercancel", this.handlePointerUp);
    this.surface.destroy();
    this.surface = null;
  }

  private tick = (): void => {
    if (!this.surface) return;
    const now = performance.now();
    this.entities.updatePositions(now);
    this.projectiles.update(now);
    if (this.highlightFadeAt > 0 && now > this.highlightFadeAt) {
      this.highlight.removeChildren();
      this.highlightFadeAt = 0;
    }
    if (this.targetMobId) {
      const pos = this.entities.getScreenPos(this.targetMobId);
      if (pos) {
        this.targetRing.position.set(pos.sx, pos.sy);
        this.targetRing.visible = true;
      } else {
        this.targetRing.visible = false;
      }
    }
    const local = this.entities.getLocalScreenPos();
    if (local) {
      const w = this.surface.app.renderer.width / this.surface.app.renderer.resolution;
      const h = this.surface.app.renderer.height / this.surface.app.renderer.resolution;
      // local.sx / local.sy are in world (pre-scale) coordinates, so we
      // multiply by WORLD_SCALE to get the on-screen position of the
      // player before centering the camera on them.
      this.surface.world.position.set(
        w / 2 - local.sx * WORLD_SCALE,
        h / 2 - local.sy * WORLD_SCALE,
      );
    }
  };

  private eventToTile(ev: PointerEvent): { tx: number; ty: number } | null {
    if (!this.surface) return null;
    const rect = this.surface.app.canvas.getBoundingClientRect();
    const cx = ev.clientX - rect.left;
    const cy = ev.clientY - rect.top;
    // Reverse the world container's translation then its scale, so we get
    // back the pre-scale world coordinates that pixelToTile expects.
    const wx = (cx - this.surface.world.x) / WORLD_SCALE;
    const wy = (cy - this.surface.world.y) / WORLD_SCALE;
    return pixelToTile(wx, wy);
  }

  private handlePointerDown = (ev: PointerEvent): void => {
    if (ev.button !== 0) return;
    const tile = this.eventToTile(ev);
    if (!tile) return;
    this.pointerDownId = ev.pointerId;
    try {
      this.surface?.app.canvas.setPointerCapture(ev.pointerId);
    } catch {
      /* ignore — capture not strictly required */
    }
    this.callbacks.onPointerDown?.(tile.tx, tile.ty);
  };

  private handlePointerMove = (ev: PointerEvent): void => {
    if (this.pointerDownId === null) return;
    const tile = this.eventToTile(ev);
    if (!tile) return;
    this.callbacks.onPointerMove?.(tile.tx, tile.ty);
  };

  private handlePointerUp = (ev: PointerEvent): void => {
    if (this.pointerDownId === null) return;
    try {
      this.surface?.app.canvas.releasePointerCapture(ev.pointerId);
    } catch {
      /* ignore */
    }
    this.pointerDownId = null;
    this.callbacks.onPointerUp?.();
  };
}

export type { FederatedPointerEvent };

function makeTargetRing(): Graphics {
  const g = new Graphics();
  const w = TILE_W * 0.55;
  const h = TILE_H * 0.55;
  g.ellipse(0, 0, w, h)
    .stroke({ color: 0xd4af37, alpha: 0.95, width: 2 });
  return g;
}
